// re.rejouis.fr — vocabulaire d'états RÉEL (option de variante "État" Shopify)
// et calcul d'estimation à partir des prix de variante par état.
//
// Source : inspection catalogue 2026-06-12. Valeurs observées (option "État") :
//   Neuf · Certifié Jamais utilisé · Parfait état · Très bon état · État correct
//   · Gueule cassée
//
// « Gueule cassée » = non fonctionnel → INÉLIGIBLE à la reprise (→ don).
// (cf. ARCHITECTURE.md : fonctionnel est une condition, pas un grade décoté.)

import { calculeCredit } from './pricing.js';
import { boostPrio } from './prio.js';

// Ordre du meilleur au moins bon, pour les états ÉLIGIBLES à la reprise.
export const ETATS_REPRISE = [
  'Neuf',
  'Certifié Jamais utilisé',
  'Parfait état',
  'Très bon état',
  'État correct',
];

// État exclu de la reprise (bascule vers le don).
export const ETATS_INELIGIBLES = new Set(['Gueule cassée']);

// Description courte par état (affichage questionnaire / badges).
export const ETAT_DESC = {
  'Neuf': 'Sous scellé, jamais ouvert',
  'Certifié Jamais utilisé': 'Boîte ouverte, jouet intact',
  'Parfait état': 'Aucun défaut visible',
  'Très bon état': 'Légers défauts, silicone impeccable',
  'État correct': 'Défauts visibles, sans impact',
  'Gueule cassée': 'Défaut fonctionnel',
};

const rang = (etat) => {
  const i = ETATS_REPRISE.indexOf(etat);
  return i === -1 ? 99 : i;
};

// Barème boutique : prix de revente d'un état rapporté au prix Neuf. Relevé sur
// le catalogue le 2026-10-13 (médianes : Certifié ≈ Parfait × 1,29, Neuf ≈
// Parfait × 1,43, Très bon ≈ Parfait × 0,71). Aucune variante « État correct »
// n'existe encore : 35 % est une hypothèse prudente (moitié de Parfait).
// Sert quand la fiche n'a PAS de variante pour l'état déclaré — 194 fiches sur
// 207 n'ont pas de variante Neuf. Sans ça, un jouet scellé était payé au prix de
// la variante la plus proche (souvent Parfait état), comme un jouet ouvert.
export const BAREME_ETAT = {
  'Neuf': 1,
  'Certifié Jamais utilisé': 0.9,
  'Parfait état': 0.7,
  'Très bon état': 0.5,
  'État correct': 0.35,
};

/**
 * Prix de revente de `etatCible`, extrapolé depuis une variante d'un autre état
 * via le barème. Même état (ou état hors barème) → prix de la variante tel quel.
 */
export function prixPourEtat(prixVariante, etatVariante, etatCible) {
  const p = Number(prixVariante) || 0;
  const de = BAREME_ETAT[etatVariante];
  const vers = BAREME_ETAT[etatCible];
  if (!de || !vers || etatVariante === etatCible) return p;
  return Math.round((p * vers) / de * 100) / 100;
}

// Électronique ou non — sert uniquement à adapter la formulation des
// questions d'état, jamais le calcul du crédit. Critère en couches, car la
// catégorie seule est ambiguë (un « Anneau pénien » ou un « Plug anal » peut
// être vibrant ou non) :
//   1. tag explicite « Manuel » / « Électronique » → override (pour corriger
//      à la main les modèles que le titre ne révèle pas : Atom, Diamo…)
//   2. catégorie clairement électronique (vibros, stimulateurs, sex machine)
//   3. sinon, indices dans le titre/tags (vibrant, connecté, télécommandé…),
//      avec négation « non vibrant ».
const CAT_ELEC = /vibr|stimulateur|sex machine/i;
const TXT_ELEC = /vibr|stimulateur|connect|t[ée]l[ée]command|rechargeable|\bpulse\b/i;
const TXT_MANUEL = /non[\s-]*vibrant|manuel/i;

// Tag d'override staff « Reprise prioritaire » (posé dans Shopify) : modèle à
// rotation très rapide (affichage seulement depuis le 2026-10-13 : plus de seuil de prix)
// (ex. Satisfyer Pro 2 Gen 2). Même famille d'overrides que Manuel/Électronique.
const TAG_PRIORITAIRE = /^reprise[\s-]?prioritaire$/i;

export function estPrioritaireReprise(produit) {
  return (produit?.tags || []).some((t) => TAG_PRIORITAIRE.test(String(t).trim()));
}

// Stock du MODÈLE : somme de toutes les variantes (états, couleurs, tailles).
// C'est la bonne maille face aux ventes 90 j, qui sont comptées par produit.
// `stockModele` est fourni par mapProduit ; le calcul de secours couvre les
// formes de produit construites à la main (tests, scan) qui ne l'ont pas.
export function stockDuModele(produit) {
  if (Number.isFinite(produit?.stockModele)) return produit.stockModele;
  return (produit?.variants || []).reduce((s, v) => s + (Number(v.stock) || 0), 0);
}

export function estElectronique(produit) {
  const tags = (produit?.tags || []).map((t) => String(t).trim());
  if (tags.some((t) => /^manuel$/i.test(t))) return false;
  if (tags.some((t) => /^(électronique|electronique|connecté|connecte)$/i.test(t))) return true;
  if (CAT_ELEC.test(produit?.categorie || '')) return true;
  const texte = `${produit?.titre || ''} ${tags.join(' ')}`;
  if (TXT_MANUEL.test(texte)) return false;
  return TXT_ELEC.test(texte);
}

/**
 * Pour un produit (forme renvoyée par catalog.js), calcule l'estimation de
 * crédit par état éligible + la fourchette AFFICHABLE. boostFidelite par défaut 1.
 * La fourchette affichée sur les cartes est celle des crédits RÉELLEMENT
 * calculés (du moins bon état éligible au meilleur) — pas le plafond théorique
 * de 25 %. Avant la refonte du taux, le calcul écrasait tout sur le plancher,
 * d'où une fourchette théorique qui promettait un montant que le questionnaire
 * ne servait jamais ; maintenant que le taux discrimine par la rotation, la
 * carte peut annoncer ce que le client touchera vraiment (la fidélité et les
 * boost Prio ne peuvent que le revaloriser au-dessus).
 * Boost Prio (modèle recherché) lu sur le produit (prio.js) : min/max sont boostés,
 * minSansBoost/maxSansBoost gardent le montant normal (prix barré).
 * @returns {{ parEtat:Array, min:number, max:number, eligibleReprise:boolean }}
 */
export function estimerProduit(produit, { boostFidelite = 1 } = {}) {
  const boost = boostPrio(produit);
  const prioritaire = estPrioritaireReprise(produit);
  const ventes90 = produit?.ventes90 ?? 0;
  const stockModele = stockDuModele(produit);
  // une variante "représentative" par état (la moins chère pour cet état,
  // toutes couleurs/tailles confondues) — l'état pilote le prix de reprise.
  const parEtatMap = new Map();
  for (const v of produit.variants || []) {
    if (!v.etat || ETATS_INELIGIBLES.has(v.etat)) continue;
    if (!ETATS_REPRISE.includes(v.etat)) continue;
    const prev = parEtatMap.get(v.etat);
    if (!prev || v.prix < prev.prix) parEtatMap.set(v.etat, v);
  }

  const parEtat = [...parEtatMap.entries()]
    .map(([etat, v]) => {
      const r = calculeCredit({
        prix: v.prix, stock: stockModele, ventes90, boostFidelite, boostPrio: boost, prioritaire,
      });
      return { etat, prix: v.prix, credit: r.credit, creditSansBoost: r.creditSansBoost, taux: r.taux, eligible: r.eligibleReprise };
    })
    .sort((a, b) => rang(a.etat) - rang(b.etat));

  // Fourchette affichée = [crédit du moins bon état éligible, crédit du meilleur].
  const eligibles = parEtat.filter((e) => e.eligible);
  const credits = eligibles.map((e) => e.credit);
  const min = credits.length ? Math.min(...credits) : 0;
  const max = credits.length ? Math.max(...credits) : 0;

  const sansBoost = eligibles.map((e) => e.creditSansBoost);
  return {
    parEtat, min, max, eligibleReprise: eligibles.length > 0, boost,
    minSansBoost: sansBoost.length ? Math.min(...sansBoost) : 0,
    maxSansBoost: sansBoost.length ? Math.max(...sansBoost) : 0,
  };
}

// ── Mapping des réponses du questionnaire d'état → vocabulaire d'état réel ──
// Renvoie l'état Shopify déclaré, ou null si non fonctionnel (→ don).
// Règle de confiance : on ne propose PAS « Certifié Jamais utilisé » au client.
// Soit la boîte est scellée → Neuf, soit elle est ouverte → état reconditionné.
export function etatDepuisReponses(rep) {
  if (rep.fonctionne === 'non') return null; // inéligible → don
  if (rep.servi === 'scelle') return 'Neuf';
  // boîte ouverte (rep.servi === 'oui') → évalué comme reconditionné, selon les traces
  if (rep.traces === 'aucune') return 'Parfait état';
  if (rep.traces === 'legeres') return 'Très bon état';
  if (rep.traces === 'marquees') return 'État correct';
  return 'Parfait état'; // défaut prudent si parcours incomplet
}

/**
 * Estimation pour un état déclaré : choisit la variante représentative de cet
 * état (exacte si dispo, sinon l'état éligible le plus proche par rang) et
 * calcule le crédit (le modèle — plafond 10 %, plancher incitatif, malus
 * accessoires −15 %, éligibilité — est entièrement dans calculeCredit).
 * @returns {{ variant, etatUtilise, etatDeclare, credit, prix, taux, eligible }}
 */
export function estimerPourEtat(produit, etatDeclare, { accessoiresIncomplets = false, boostFidelite = 1 } = {}) {
  const parEtat = new Map();
  for (const v of produit.variants || []) {
    if (!v.etat || ETATS_INELIGIBLES.has(v.etat) || !ETATS_REPRISE.includes(v.etat)) continue;
    const prev = parEtat.get(v.etat);
    if (!prev || v.prix < prev.prix) parEtat.set(v.etat, v);
  }
  if (parEtat.size === 0) {
    return { variant: null, etatUtilise: null, etatDeclare, credit: 0, creditSansBoost: 0, boost: 0, prix: 0, taux: 0, eligible: false };
  }

  // état exact si dispo, sinon le plus proche par rang (préfère le plus proche)
  let etatUtilise = parEtat.has(etatDeclare) ? etatDeclare : null;
  if (!etatUtilise) {
    const cible = rang(etatDeclare);
    let best = null;
    for (const etat of parEtat.keys()) {
      const d = Math.abs(rang(etat) - cible);
      if (!best || d < best.d) best = { etat, d };
    }
    etatUtilise = best.etat;
  }

  const v = parEtat.get(etatUtilise);
  // État déclaré absent des variantes → prix extrapolé par le barème depuis la
  // variante la plus proche (qui reste celle rattachée à la ligne).
  const prix = prixPourEtat(v.prix, etatUtilise, etatDeclare);
  // Le stock et les ventes sont des signaux de MODÈLE (toutes variantes
  // confondues) : les ventes 90 j sont comptées au niveau produit, la
  // couverture doit donc l'être aussi — sinon on comparerait le stock d'une
  // seule couleur à la vélocité de toutes.
  // Boost Prio lu sur le produit : mêmes données que le recalcul serveur (demande.js).
  // Les fiches provisoires n'ont pas de metafield : jamais boostées.
  const boost = boostPrio(produit);
  const r = calculeCredit({
    prix, stock: stockDuModele(produit), ventes90: produit?.ventes90 ?? 0,
    boostFidelite, boostPrio: boost, accessoiresIncomplets,
    prioritaire: estPrioritaireReprise(produit),
  });
  return { variant: v, etatUtilise, etatDeclare, credit: r.credit, creditSansBoost: r.creditSansBoost, boost, prix, taux: r.taux, eligible: r.eligibleReprise };
}
